import React, { useContext, useMemo } from "react";
import { Box } from "@mui/material";
import { get, isEmpty } from "lodash-es";
import { selectServiceModule } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { useSelector } from "react-redux";
import useNavigateEntity from "hooks/useNavigateEntity";
import EmptyImgStatus from "components/EmptyState/EmptyImageStatus";
import DateArea from "features/metadesk/CommunityAnalysis/components/DateArea";
import { useIntl } from "react-intl";
import useCommunity from "../hooks/useDAppTrackingState.hooks";
import DAppTrackingContext from "features/metadesk/DAppTracking/contexts/DAppTracking.context";
import { analysisColors } from "features/configure/chartColors";
import { useDebounceEffect } from "components/ConnectWallet/ConnectWallet";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import LoadingComponent from "components/LoadingComponent/LoadingComponent";
import { StyledWhiteContainer } from "features/metadesk/CommunityAnalysis/CommunityAnalysis.styles";
import ChartContainer from "features/metadesk/CommunityAnalysis/components/chart/ChartContainer";
import DataBlock from "features/metadesk/CommunityAnalysis/components/DataBlock";
import {
  AutoCompleteDropdown,
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { useParams } from "react-router-dom";

const timeFrameOptions = [
  { label: "Daily", value: "days" },
  { label: "Weekly", value: "weeks" },
  { label: "Monthly", value: "months" },
];

const viewTargetOptions = [
  { label: "All", value: "" },
  { label: "BSC Contract", value: "bsc" },
  { label: "ETH Contract", value: "eth" },
];

const DAppTrackingContent = () => {
  const { dateRange, viewTarget, setViewTarget } =
    useContext(DAppTrackingContext);
  const { subEntityName } = useParams();

  const { navigateEntity } = useNavigateEntity();
  const { formatMessage } = useIntl();
  const serviceModule = useSelector(selectServiceModule);
  // const isConnected = get(serviceModule, "discordAnalytics.connected"); // TODO 是否有需要判斷有連結之類的
  const {
    contractOptions,
    contractOptionsLoading,
    fetchContractOptions,
    fetchBasicData,
    fetchNewWalletData,
    fetchCohortData,
    fetchWalletActivityOverviewData,
    basicData,
    basicDataLoading,
    newWalletData,
    newWalletDataLoading,
    cohortData,
    cohortDataLoading,
    highLevelDataArray,
    walletActivityOverviewData,
    walletActivityOverviewDataLoading,
  } = useCommunity();

  const colors = analysisColors.small;
  const [discordColor, telegramColor] = colors;
  const color = discordColor;
  const type = "discord";

  const handleChangeViewTarget = (event, newValue) => {
    // setViewTarget(value.value);
    setViewTarget(newValue.map((item) => item.value));
  };

  // 根據 id 決定 tooltip 內容

  const newWalletSeriesData = useMemo(() => {
    return isEmpty(newWalletData)
      ? []
      : [
          {
            name: formatMessage({ id: "global.metrics.newWallets" }),
            type: "line",
            areaStyle: {
              color: "transparent",
            },
            lineStyle: {
              color: colors[0], // 自訂折線顏色
              width: 2, // 線條寬度
            },
            itemStyle: {
              color: colors[0], // 節點的顏色
              borderColor: colors[0], // 節點的邊框顏色
              borderWidth: 2, // 邊框寬度
            },
            emphasis: {
              focus: "series",
              areaStyle: {
                color: colors[0], // 自訂區域填充顏色
                opacity: 0.1,
              },
            },
            data: newWalletData.map((item) => item.newWallets),
          },
        ];
  }, [newWalletData]);

  const newWalletDataForChart = useMemo(() => {
    return {
      showDate: newWalletData.map((item) => item.date),
      seriesData: newWalletSeriesData,
    };
  }, [newWalletSeriesData, newWalletData]);

  const walletActivityOverviewDataSeriesData = useMemo(() => {
    return isEmpty(walletActivityOverviewData)
      ? []
      : [
          {
            name: formatMessage({ id: "global.legend.uniqueWallet" }),
            type: "line",
            areaStyle: {
              color: "transparent",
            },
            lineStyle: {
              color: colors[0], // 自訂折線顏色
              width: 2, // 線條寬度
            },
            itemStyle: {
              color: colors[0], // 節點的顏色
              borderColor: colors[0], // 節點的邊框顏色
              borderWidth: 2, // 邊框寬度
            },
            emphasis: {
              focus: "series",
              areaStyle: {
                color: colors[1], // 自訂區域填充顏色
                opacity: 0.1,
              },
            },
            data: walletActivityOverviewData.map((item) => item.uniqueWallets),
          },
          {
            name: formatMessage({ id: "global.demographic.transaction" }),
            type: "line",
            areaStyle: {
              color: "transparent",
            },
            lineStyle: {
              color: colors[1], // 自訂折線顏色
              width: 2, // 線條寬度
            },
            itemStyle: {
              color: colors[1], // 節點的顏色
              borderColor: colors[1], // 節點的邊框顏色
              borderWidth: 2, // 邊框寬度
            },
            emphasis: {
              focus: "series",
              areaStyle: {
                color: colors[1], // 自訂區域填充顏色
                opacity: 0.1,
              },
            },
            data: walletActivityOverviewData.map((item) => item.transactions),
          },
        ];
  }, [walletActivityOverviewData]);

  const walletActivityOverviewDataForChart = useMemo(() => {
    return {
      showDate: walletActivityOverviewData.map((item) => item.date),
      seriesData: walletActivityOverviewDataSeriesData,
    };
  }, [walletActivityOverviewDataSeriesData, walletActivityOverviewData]);

  useDebounceEffect(
    () => {
      fetchContractOptions();
    },
    50,
    []
  );

  useDebounceEffect(
    () => {
      if (!isEmpty(contractOptions)) {
        const startDate = dateRange[0];
        const endDate = dateRange[1];
        fetchBasicData({ viewTarget, from: startDate, to: endDate });
        fetchWalletActivityOverviewData({
          viewTarget,
          from: startDate,
          to: endDate,
        });
        fetchNewWalletData({ viewTarget, from: startDate, to: endDate });
        fetchCohortData({ viewTarget, from: startDate, to: endDate });
      }
    },
    50,
    [type, contractOptions, viewTarget, dateRange]
  );

  // 沒有contract 也不是觀察subentity的狀況 不要進入emptyStatus
  if (
    !subEntityName &&
    !contractOptionsLoading &&
    contractOptions.length <= 1
  ) {
    return (
      <>
        <DateArea
          sx={{ marginBottom: "8px" }}
          context={DAppTrackingContext}
        ></DateArea>
        <EmptyImgStatus
          type="campaign"
          title={formatMessage({ id: "dAppTracking.emptyStatus.title" })}
          description={formatMessage({
            id: "dAppTracking.emptyStatus.description",
          })}
          backgroundImg={"DApp_emptyBackground"}
          buttonText={formatMessage({
            id: "dAppTracking.emptyStatus.btn",
          })}
          onClick={() =>
            navigateEntity(
              "/analytics/settings/dapp-activity-tracking/add-contract"
            )
          }
        />
      </>
    );
  }

  return (
    <Box>
      <DateArea
        sx={{ marginBottom: "8px", padding: "14px 16px" }}
        context={DAppTrackingContext}
        quickSelectOptions={[
          { days: 1, label: formatMessage({ id: "datePicker.range.day" }) },
          { days: 7, label: formatMessage({ id: "datePicker.range.week" }) },
          { days: 30, label: formatMessage({ id: "datePicker.range.month" }) },
          {
            days: "custom",
            label: formatMessage({ id: "datePicker.range.custom" }),
          },
        ]}
        datePickerProps={{
          minDate: new Date().setDate(new Date().getDate() - 30),
          maxDate: new Date(),
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          <Box
            sx={{ fontSize: "12px", marginRight: "5px" }}
            color={(theme) => theme.customColors.grey[500]}
          >
            {formatMessage({ id: "dAppTracking.instance.labelName" })} :
          </Box>
          <Box width={"400px"}>
            <AutoCompleteDropdown
              multiple
              value={contractOptions.filter((option) =>
                viewTarget.includes(option.value)
              )}
              placeholder={formatMessage({ id: "menu.placeholder.select" })}
              options={contractOptions}
              onChange={handleChangeViewTarget}
              size={SIZE.MEDIUM}
              limitTags={1}
            />
          </Box>
        </Box>
      </DateArea>
      <Box component={Grid} container spacing={1} mb={1}>
        <>
          {highLevelDataArray.length > 0
            ? highLevelDataArray.map((highLevelData, index) => (
                <Grid item lg={4} xs={6} key={index}>
                  <DataBlock
                    data={highLevelData}
                    isLoading={basicDataLoading}
                  ></DataBlock>
                </Grid>
              ))
            : Array.from({ length: 3 }).map((index) => (
                <Grid item lg={4} xs={12} key={index}>
                  <DataBlock
                    data={{ isEmpty: true }}
                    isLoading={basicDataLoading}
                  ></DataBlock>
                </Grid>
              ))}
        </>
      </Box>

      <Box component={Grid} container spacing={1}>
        <Grid item xs={12}>
          <StyledWhiteContainer>
            <ChartContainer
              title={formatMessage({
                id: "dAppTracking.chart.activityOverview",
              })}
              chartStyle="line"
              chartData={walletActivityOverviewDataForChart}
              isLoading={walletActivityOverviewDataLoading}
            ></ChartContainer>
          </StyledWhiteContainer>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              fontSize: "20px",
              fontWeight: "600",
              marginTop: "24px",
            }}
            color={(theme) => theme.customColors.grey[700]}
          >
            {formatMessage({ id: "dAppTracking.growth&retention.title" })}
          </Box>
        </Grid>
        <Grid item xs={12} lg={6}>
          <StyledWhiteContainer>
            <ChartContainer
              title={formatMessage({
                id: "global.metrics.newWallets",
              })}
              tooltipText={formatMessage({
                id: "metrics.newWallet.tooltip",
              })}
              chartStyle="line"
              chartData={newWalletDataForChart}
              isLoading={newWalletDataLoading}
              context={DAppTrackingContext}
              options={{ showTotalNumber: true }}
            ></ChartContainer>
          </StyledWhiteContainer>
        </Grid>

        <Grid item xs={12} lg={6}>
          <StyledWhiteContainer>
            <ChartContainer
              chartStyle="heatmap"
              tooltipText={formatMessage({
                id: "communityAnalysis.chart.retentionCohort.tooltip",
              })}
              title={formatMessage({
                id: "communityAnalysis.chart.retentionCohort",
              })}
              chartData={cohortData}
              isLoading={cohortDataLoading}
              heatColor={color}
              context={DAppTrackingContext}
            >
              <Box width={"160px"}></Box>
            </ChartContainer>
          </StyledWhiteContainer>
        </Grid>
      </Box>
    </Box>
  );
};

export default DAppTrackingContent;
